import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Crown, Swords } from "lucide-react";
import api from "../lib/api";
import Reveal from "../components/Reveal";
import { useSeo, breadcrumbLd } from "../lib/seo";

export default function Leaderboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/leaderboard").then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useSeo({
    title: "Global Leaderboard & Face Battles",
    description: "See the highest AI facial-analysis scores worldwide. Climb the Aesthetiq leaderboard and challenge others in Face Battles.",
    path: "/leaderboard",
    jsonLd: breadcrumbLd([{ name: "Home", path: "/" }, { name: "Leaderboard", path: "/leaderboard" }]),
  });

  const medal = (rank) => (rank === 1 ? "text-primary" : rank === 2 ? "text-foreground" : rank === 3 ? "text-success" : "text-muted-foreground");

  return (
    <div className="container py-16">
      <Reveal>
        <div className="text-center">
          <span className="label-eyebrow flex items-center justify-center gap-2"><Swords className="h-4 w-4" /> Face Battles</span>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl">Global Leaderboard</h1>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">The highest-scoring public FaceCards on Aesthetiq. Make your scan public to claim your rank.</p>
          <Link to="/" className="mt-6 inline-block rounded-md bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition hover:brightness-110" data-testid="leaderboard-scan-btn">
            Scan & enter the ranking
          </Link>
        </div>
      </Reveal>

      <div className="mx-auto mt-12 max-w-3xl overflow-hidden rounded-xl border border-border bg-card">
        <div className="grid grid-cols-[60px_1fr_auto_auto] gap-4 border-b border-border bg-secondary/30 px-5 py-3 text-xs uppercase tracking-wider text-muted-foreground">
          <span>Rank</span><span>Name</span><span className="text-right">Potential</span><span className="text-right">Score</span>
        </div>
        {loading ? (
          <p className="px-5 py-10 text-center text-sm text-muted-foreground">Loading rankings…</p>
        ) : rows.length === 0 ? (
          <p className="px-5 py-10 text-center text-sm text-muted-foreground">No public scores yet. Be the first to claim #1.</p>
        ) : (
          rows.map((r) => (
            <Link
              to={`/r/${r.id}`}
              key={r.id}
              data-testid={`leaderboard-row-${r.rank}`}
              className="grid grid-cols-[60px_1fr_auto_auto] items-center gap-4 border-b border-border px-5 py-4 transition last:border-0 hover:bg-secondary/30"
            >
              <span className={`flex items-center gap-1 font-mono text-lg font-bold ${medal(r.rank)}`}>
                {r.rank <= 3 && <Crown className="h-4 w-4" />}{r.rank}
              </span>
              <span className="truncate">
                <span className="font-medium">{r.display_name}</span>
                <span className="ml-2 text-xs text-muted-foreground">{r.face_shape}</span>
              </span>
              <span className="text-right font-mono text-sm text-muted-foreground">{r.potential_score}</span>
              <span className="text-right font-mono text-lg font-bold text-primary">{r.overall_score}</span>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
